import { Injectable } from '@angular/core';

export interface FieldDecision {
    field: string;
    changed: boolean;
    autoApply: boolean;
    reason: string;
}

// TR-21: demand on the days ADDED by an extension of the end of the service.
// `color` null = could not be verified; `diasRojos` are YYYY-MM-DD keys.
export interface ExtensionAvailability {
    color: string;
    tipo: string;
    diasRojos: string[];
}

export interface MatrixResult {
    requiresSolicitud: boolean;
    decisions: FieldDecision[];
    legendText: string;
}

@Injectable()
export class ConfirmacionChangeMatrixService {

    private static readonly DAYS_GATE = 15;
    private static readonly MAX_DISTANCE_KM = 10;
    private static readonly MIN_START_EARLIER_MIN = 15;
    private static readonly EARTH_RADIUS_KM = 6371;

    evaluateChanges(params: {
        original: any;
        edited: any;
        originalUnidades: any[];
        editedUnidades: any[];
        originalPuntosIntermedios: any[];
        editedPuntosIntermedios: any[];
        newDateAvailability: { color: string; razon: string } | null;
        newUnitAvailability: { color: string; razon: string } | null;
        extensionAvailability?: ExtensionAvailability | null;
    }): MatrixResult {
        const { original, edited, originalUnidades, editedUnidades,
                originalPuntosIntermedios, editedPuntosIntermedios,
                newDateAvailability, newUnitAvailability } = params;
        const extensionAvailability = params.extensionAvailability || null;

        const daysToService = this.calculateDaysToService(edited.inicioDeServicio);
        const decisions: FieldDecision[] = [];

        const unidadDecision = this.evaluateUnidad(originalUnidades, editedUnidades, newUnitAvailability);
        if (unidadDecision) decisions.push(unidadDecision);

        const diaDecision = this.evaluateDiaServicio(original, edited, newDateAvailability);
        if (diaDecision) decisions.push(diaDecision);

        const puntoInicioDecision = this.evaluatePuntoInicioFin(
            original.origen, edited.origen, 'Punto de inicio', daysToService
        );
        if (puntoInicioDecision) decisions.push(puntoInicioDecision);

        const puntoFinDecision = this.evaluatePuntoInicioFin(
            original.destino, edited.destino, 'Punto de fin', daysToService
        );
        if (puntoFinDecision) decisions.push(puntoFinDecision);

        const serviceInProgress = this.isServiceInProgress(original.inicioDeServicio);

        const horaInicioDecision = this.evaluateHoraInicio(
            original.inicioDeServicio, edited.inicioDeServicio, daysToService, serviceInProgress
        );
        if (horaInicioDecision) decisions.push(horaInicioDecision);

        const horaFinDecision = this.evaluateHoraFin(
            original.inicioDeServicio, original.finDeServicio,
            edited.inicioDeServicio, edited.finDeServicio,
            daysToService, serviceInProgress, extensionAvailability
        );
        if (horaFinDecision) decisions.push(horaFinDecision);

        const timeChanged = (horaInicioDecision && horaInicioDecision.changed) ||
                            (horaFinDecision && horaFinDecision.changed);
        const intermediosDecision = this.evaluatePuntosIntermedios(
            originalPuntosIntermedios, editedPuntosIntermedios, timeChanged, daysToService
        );
        if (intermediosDecision) decisions.push(intermediosDecision);

        const changedDecisions = decisions.filter(function(d) { return d.changed; });
        const requiresSolicitud = changedDecisions.some(function(d) { return !d.autoApply; });
        const legendText = this.buildLegendText(changedDecisions, requiresSolicitud);

        return { requiresSolicitud: requiresSolicitud, decisions: decisions, legendText: legendText };
    }

    determinePriority(inicioDeServicio: number): string {
        const days = this.calculateDaysToService(inicioDeServicio);
        if (days <= 3) return 'Urgente';
        if (days <= 7) return 'Alta';
        return 'Normal';
    }

    private evaluateUnidad(
        originalUnidades: any[],
        editedUnidades: any[],
        newUnitAvailability: { color: string; razon: string } | null
    ): FieldDecision | null {
        const changed = this.unidadesChanged(originalUnidades, editedUnidades);
        if (!changed) return { field: 'Unidad', changed: false, autoApply: true, reason: '' };

        if (!newUnitAvailability) {
            return { field: 'Unidad', changed: true, autoApply: false, reason: 'No se pudo verificar disponibilidad' };
        }

        var autoApply = newUnitAvailability.color === 'green';
        var reason = autoApply ? 'Disponibilidad verificada' : (newUnitAvailability.razon || 'Sin disponibilidad');
        return { field: 'Unidad', changed: true, autoApply: autoApply, reason: reason };
    }

    private evaluateDiaServicio(
        original: any,
        edited: any,
        newDateAvailability: { color: string; razon: string } | null
    ): FieldDecision | null {
        var originalDate = this.extractDateString(original.inicioDeServicio);
        var editedDate = this.extractDateString(edited.inicioDeServicio);

        if (originalDate === editedDate) {
            return { field: 'Día de servicio', changed: false, autoApply: true, reason: '' };
        }

        if (!newDateAvailability) {
            return { field: 'Día de servicio', changed: true, autoApply: false, reason: 'No se pudo verificar disponibilidad' };
        }

        var autoApply = newDateAvailability.color === 'green';
        var reason = autoApply ? 'Disponibilidad verificada' : (newDateAvailability.razon || 'Sin disponibilidad');
        return { field: 'Día de servicio', changed: true, autoApply: autoApply, reason: reason };
    }

    private evaluatePuntoInicioFin(
        originalLocation: any,
        editedLocation: any,
        fieldName: string,
        daysToService: number
    ): FieldDecision | null {
        if (!originalLocation || !editedLocation) return null;

        var coordsOriginal = originalLocation.coordenadas;
        var coordsEdited = editedLocation.coordenadas;

        if (!coordsOriginal || !coordsEdited) return null;

        var aliasChanged = (originalLocation.alias || '') !== (editedLocation.alias || '');
        var direccionChanged = (originalLocation.direccion || '') !== (editedLocation.direccion || '');
        var coordsChanged = coordsOriginal.lat !== coordsEdited.lat || coordsOriginal.lng !== coordsEdited.lng;

        if (!aliasChanged && !direccionChanged && !coordsChanged) {
            return { field: fieldName, changed: false, autoApply: true, reason: '' };
        }

        if (daysToService > ConfirmacionChangeMatrixService.DAYS_GATE) {
            return { field: fieldName, changed: true, autoApply: true, reason: 'Más de 15 días para el servicio' };
        }

        var distance = this.haversineDistanceKm(coordsOriginal, coordsEdited);
        var autoApply = distance <= ConfirmacionChangeMatrixService.MAX_DISTANCE_KM;
        var reason = autoApply
            ? 'Distancia de ' + distance.toFixed(1) + ' km (≤10 km)'
            : 'Distancia de ' + distance.toFixed(1) + ' km (>10 km)';

        return { field: fieldName, changed: true, autoApply: autoApply, reason: reason };
    }

    // Gates that short-circuit both time evaluations before their field-specific rule runs.
    private evaluateTimeGates(
        fieldName: string,
        daysToService: number,
        serviceInProgress: boolean
    ): FieldDecision | null {
        if (serviceInProgress) {
            return { field: fieldName, changed: true, autoApply: true, reason: 'Servicio en curso — el cambio aplica directamente' };
        }

        if (daysToService > ConfirmacionChangeMatrixService.DAYS_GATE) {
            return { field: fieldName, changed: true, autoApply: true, reason: 'Más de 15 días para el servicio' };
        }

        return null;
    }

    private evaluateHoraInicio(
        originalInicio: number,
        editedInicio: number,
        daysToService: number,
        serviceInProgress: boolean
    ): FieldDecision | null {
        if (!originalInicio || !editedInicio) return null;

        // Only the time of day is judged here: moving the service to another calendar day is
        // reported by 'Día de servicio'. Evaluating the two independently means a reschedule that
        // also shifts the hour reports both changes instead of hiding the hour behind the day.
        var originalMinutes = this.extractTimeMinutes(originalInicio);
        var editedMinutes = this.extractTimeMinutes(editedInicio);

        if (originalMinutes === editedMinutes) {
            return { field: 'Hora de inicio', changed: false, autoApply: true, reason: '' };
        }

        var gateDecision = this.evaluateTimeGates('Hora de inicio', daysToService, serviceInProgress);
        if (gateDecision) return gateDecision;

        var minutesEarlier = originalMinutes - editedMinutes;
        if (minutesEarlier >= ConfirmacionChangeMatrixService.MIN_START_EARLIER_MIN) {
            return { field: 'Hora de inicio', changed: true, autoApply: false, reason: 'Inicio se adelanta ' + minutesEarlier + ' min (≥15 min)' };
        }

        var reasonInicio = minutesEarlier > 0
            ? 'Inicio se adelanta ' + minutesEarlier + ' min (<15 min)'
            : 'Inicio no se adelanta';
        return { field: 'Hora de inicio', changed: true, autoApply: true, reason: reasonInicio };
    }

    private evaluateHoraFin(
        originalInicio: number,
        originalFin: number,
        editedInicio: number,
        editedFin: number,
        daysToService: number,
        serviceInProgress: boolean,
        extensionAvailability?: ExtensionAvailability | null
    ): FieldDecision | null {
        if (!originalInicio || !originalFin || !editedInicio || !editedFin) return null;

        // The end is measured from the start's midnight, so it is judged independently of which
        // calendar day the service sits on: rescheduling the whole service to another day leaves
        // this untouched (that is 'Día de servicio'), while an end pushed onto the following day
        // is reported here even when its clock time is unchanged. Both "did it change" and "does it
        // need approval" read this same offset — comparing absolute epochs for the second question
        // would demand approval for a service moved to a later day that actually ends earlier, and
        // wave through one moved to an earlier day whose end runs later into the day.
        var originalOffset = this.minutesFromStartOfDay(originalInicio, originalFin);
        var editedOffset = this.minutesFromStartOfDay(editedInicio, editedFin);

        if (originalOffset === editedOffset) {
            return { field: 'Hora de fin', changed: false, autoApply: true, reason: '' };
        }

        // TR-21: an extension onto one or more new calendar days is judged by the semáforo of those
        // days BEFORE the "en curso" and "> 15 días" gates, which would otherwise let it through
        // without looking at availability. Only RED (or not being able to verify) forces a solicitud;
        // orange/green fall through to the regular rules below.
        var extensionDays = this.getExtensionDays(originalInicio, originalFin, editedInicio, editedFin);
        if (extensionDays.length > 0) {
            var extensionDecision = this.evaluateExtension(extensionDays.length, extensionAvailability || null);
            if (extensionDecision) return extensionDecision;
        }

        var gateDecision = this.evaluateTimeGates('Hora de fin', daysToService, serviceInProgress);
        if (gateDecision) return gateDecision;

        var minutesExtended = editedOffset - originalOffset;
        if (minutesExtended > 0) {
            return { field: 'Hora de fin', changed: true, autoApply: false, reason: 'Fin se extiende ' + minutesExtended + ' min más allá de lo programado' };
        }

        return { field: 'Hora de fin', changed: true, autoApply: true, reason: 'Fin no se extiende más allá de lo programado' };
    }

    private evaluateExtension(
        numDays: number,
        availability: ExtensionAvailability | null
    ): FieldDecision | null {
        var prefix = 'Extensión de ' + numDays + ' día(s)';

        if (!availability || !availability.color) {
            return { field: 'Hora de fin', changed: true, autoApply: false, reason: prefix + ': no se pudo verificar disponibilidad' };
        }

        if (availability.color === 'red') {
            var dias = (availability.diasRojos || []).map(function(key) {
                var parts = key.split('-');
                return parts[2] + '/' + parts[1] + '/' + parts[0];
            }).join(', ');
            return {
                field: 'Hora de fin',
                changed: true,
                autoApply: false,
                reason: prefix + ': ' + dias + ' en rojo para ' + (availability.tipo || 'la unidad')
            };
        }

        return null;
    }

    // Calendar days added at the end of the service: when the end falls on more calendar days after
    // the start than before, the last N days of the edited range are the extension. Moving the whole
    // service to another day keeps the span, so it is not an extension (that is 'Día de servicio').
    // Returns the local midnight (ms) of each added day, oldest first.
    getExtensionDays(originalInicio: number, originalFin: number, editedInicio: number, editedFin: number): number[] {
        if (!originalInicio || !originalFin || !editedInicio || !editedFin) return [];

        var extra = this.calendarDaysBetween(editedInicio, editedFin) - this.calendarDaysBetween(originalInicio, originalFin);
        if (extra <= 0) return [];

        var fin = new Date(editedFin);
        var days: number[] = [];
        for (var k = extra - 1; k >= 0; k--) {
            days.push(new Date(fin.getFullYear(), fin.getMonth(), fin.getDate() - k).getTime());
        }
        return days;
    }

    private calendarDaysBetween(startTimestamp: number, endTimestamp: number): number {
        var s = new Date(startTimestamp);
        var e = new Date(endTimestamp);
        var sDay = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
        var eDay = new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
        return Math.round((eDay - sDay) / (24 * 60 * 60 * 1000));
    }

    private isServiceInProgress(inicioDeServicio: number): boolean {
        if (!inicioDeServicio) return false;
        return inicioDeServicio <= new Date().getTime();
    }

    private evaluatePuntosIntermedios(
        originalPuntos: any[],
        editedPuntos: any[],
        timeAlsoChanged: boolean,
        daysToService: number
    ): FieldDecision | null {
        var changed = this.puntosIntermediosChanged(originalPuntos, editedPuntos);

        if (!changed) {
            return { field: 'Puntos intermedios', changed: false, autoApply: true, reason: '' };
        }

        if (daysToService > ConfirmacionChangeMatrixService.DAYS_GATE) {
            return { field: 'Puntos intermedios', changed: true, autoApply: true, reason: 'Más de 15 días para el servicio' };
        }

        if (!timeAlsoChanged) {
            return { field: 'Puntos intermedios', changed: true, autoApply: true, reason: 'No afecta hora de inicio/fin' };
        }

        return {
            field: 'Puntos intermedios',
            changed: true,
            autoApply: false,
            reason: 'Cambio en puntos intermedios afecta horario del servicio'
        };
    }

    haversineDistanceKm(coord1: { lat: number; lng: number }, coord2: { lat: number; lng: number }): number {
        var R = ConfirmacionChangeMatrixService.EARTH_RADIUS_KM;
        var dLat = this.toRadians(coord2.lat - coord1.lat);
        var dLon = this.toRadians(coord2.lng - coord1.lng);
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(this.toRadians(coord1.lat)) * Math.cos(this.toRadians(coord2.lat)) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    calculateDaysToService(inicioDeServicio: number): number {
        var now = new Date().getTime();
        return (inicioDeServicio - now) / (1000 * 60 * 60 * 24);
    }

    private extractDateString(timestamp: number): string {
        var d = new Date(timestamp);
        return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
    }

    private extractTimeMinutes(timestamp: number): number {
        var d = new Date(timestamp);
        return d.getHours() * 60 + d.getMinutes();
    }

    // Minutes from midnight of the start day to the end instant. Encodes both the end's clock time
    // and how many days it runs past the start, which is what makes it comparable across a
    // reschedule to another calendar day.
    private minutesFromStartOfDay(inicioTimestamp: number, finTimestamp: number): number {
        var inicio = new Date(inicioTimestamp);
        var startOfDay = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()).getTime();
        return Math.round((finTimestamp - startOfDay) / 60000);
    }

    private toRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    private unidadesChanged(original: any[], edited: any[]): boolean {
        if (!original || !edited) return false;
        if (original.length !== edited.length) return true;

        for (var i = 0; i < original.length; i++) {
            var origUnit = original[i];
            var editUnit = edited[i];
            if (!origUnit || !editUnit) return true;
            if ((origUnit.cantidad || 0) !== (editUnit.cantidad || 0)) return true;
            if ((origUnit.tipo || '') !== (editUnit.tipo || '')) return true;
        }
        return false;
    }

    private puntosIntermediosChanged(original: any[], edited: any[]): boolean {
        if (!original && !edited) return false;
        if (!original || !edited) return true;
        if (original.length !== edited.length) return true;

        for (var i = 0; i < original.length; i++) {
            var orig = original[i];
            var edit = edited[i];
            if ((orig.alias || '') !== (edit.alias || '')) return true;
            if ((orig.direccion || '') !== (edit.direccion || '')) return true;
            if (orig.coordenadas && edit.coordenadas) {
                if (orig.coordenadas.lat !== edit.coordenadas.lat) return true;
                if (orig.coordenadas.lng !== edit.coordenadas.lng) return true;
            }
        }
        return false;
    }

    private buildLegendText(changedDecisions: FieldDecision[], requiresSolicitud: boolean): string {
        if (changedDecisions.length === 0) return '';

        if (!requiresSolicitud) {
            return 'Todos los cambios pasan automáticamente.';
        }

        var solicitudFields = changedDecisions
            .filter(function(d) { return !d.autoApply; })
            .map(function(d) { return d.field; });

        return 'Se requiere solicitud de disponibilidad por: ' + solicitudFields.join(', ') + '.';
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/confirmacion-change-matrix.service.ts